import { ref } from 'vue'
import { defineStore } from 'pinia'
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  type User,
} from 'firebase/auth'
import { auth } from '@/firebase/config'
import { TenantSearch } from '@/services/tenant/TenantSearch'
import type { Tenant } from '@/types/Tenant'

export const useUserStore = defineStore('user', () => {
  const user = ref<User | null>(null)
  const loading = ref(true)
  const error = ref<string | null>(null)

  // Initialize the auth listener
  onAuthStateChanged(auth, (firebaseUser) => {
    user.value = firebaseUser
    loading.value = false
  })

  // Register a new user
  async function register(email: string, password: string) {
    try {
      error.value = null
      await createUserWithEmailAndPassword(auth, email, password)
    } catch (e) {
      error.value = e instanceof Error ? e.message : String(e)
      throw e
    }
  }

  // Resolve tenant by email domain
  async function resolveTenantByEmail(email: string): Promise<Tenant | null> {
    try {
      const domain = email.split('@')[1]
      if (!domain) return null

      const { getTenantSearch } = await import('@/services/serviceFactory')
      const search = await getTenantSearch({})
      search.byEmailDomain(domain)
      const results = await search.run()
      return results.length > 0 ? results[0] : null
    } catch (e) {
      console.error('Error resolving tenant by email:', e)
      return null
    }
  }

  // Resolve tenant by slug
  async function resolveTenantBySlug(slug: string): Promise<Tenant | null> {
    try {
      const { getTenantSearch } = await import('@/services/serviceFactory')
      const search = await getTenantSearch({})
      search.bySlug(slug)
      const results = await search.run()
      return results.length > 0 ? results[0] : null
    } catch (e) {
      console.error('Error resolving tenant by slug:', e)
      return null
    }
  }

  // Login with email/password and tenant resolution
  async function login(email: string, password: string, tenantSlug?: string) {
    try {
      error.value = null

      let tenant: Tenant | null = null

      // Resolve tenant by slug if provided, otherwise by email domain
      if (tenantSlug) {
        tenant = await resolveTenantBySlug(tenantSlug)
      } else {
        tenant = await resolveTenantByEmail(email)
      }

      if (!tenant) {
        throw new Error('No tenant found for this email address')
      }

      // Set the tenant context before signing in
      ;(auth as { tenantId?: string }).tenantId = tenant.auth.tenantId

      await signInWithEmailAndPassword(auth, email, password)
    } catch (e) {
      error.value = e instanceof Error ? e.message : String(e)
      throw e
    }
  }

  // Logout
  async function logout() {
    try {
      await signOut(auth)
    } catch (e) {
      error.value = e instanceof Error ? e.message : String(e)
      throw e
    }
  }

  return {
    user,
    loading,
    error,
    register,
    login,
    logout,
    resolveTenantByEmail,
    resolveTenantBySlug,
  }
})
